import React, {FC, useState, useEffect} from 'react'
import {PmsPlanDto, PmsPlanStatus} from '../../core/pms/_models'
import type {Vessel} from '../../../Management/core/_models'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

interface Props {
  visible: boolean
  onClose: () => void
  onSubmit: (data: PmsPlanDto) => Promise<PmsPlanDto>
  vessels: Vessel[]
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(15, 23, 42, 0.45)',
  position: 'fixed',
  inset: 0,
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(2px)',
  padding: '24px',
  overflow: 'auto',
  overscrollBehavior: 'contain',
}

export const AddPlanModal: FC<Props> = ({visible, onClose, onSubmit, vessels}) => {
  const [formData, setFormData] = useState<PmsPlanDto>({
    vesselId: vessels[0]?.id || 0,
    name: '',
    description: '',
    status: 'DRAFT',
  })
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  useEffect(() => {
    if (vessels.length > 0 && !formData.vesselId) {
      setFormData((prev) => ({...prev, vesselId: vessels[0].id}))
    }
  }, [vessels])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: {[key: string]: string} = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Job Plan name is required'
    }
    if (!formData.vesselId) {
      newErrors.vesselId = 'Vessel is required'
    }
    setErrors(newErrors)
    if (Object.keys(newErrors).length === 0) {
      try {
        await onSubmit(formData)
        onClose()
      } catch (error) {
        // Error handling in parent
      }
    }
  }

  if (!visible) return null

  return (
    <div
      style={WRAP_STYLE}
      tabIndex={-1}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role='dialog'
        aria-modal='true'
        style={{
          width: 'min(1400px, 96vw)',
          height: 'min(900px, 92vh)',
          background: '#ffffff',
          borderRadius: '18px',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.22)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexShrink: 0,
          }}
        >
          <div>
            <h5
              className='mb-1'
              style={{
                margin: 0,
                color: '#111827',
                fontSize: '1.2rem',
                fontWeight: 700,
              }}
            >
              Add New Job Plan
            </h5>
            <div style={{color: '#6B7280', fontSize: '0.92rem'}}>
              Create a new PMS job plan for a vessel.
            </div>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='btn btn-sm'
            style={{
              border: '1px solid #D1D5DB',
              background: '#fff',
              color: '#374151',
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              fontSize: '18px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <form
          onSubmit={handleSubmit}
          className='d-flex flex-column flex-grow-1'
          style={{minHeight: 0}}
        >
          <div
            className='flex-grow-1 overflow-auto px-4 py-3'
            style={{maxHeight: 'calc(92vh - 140px)'}}
          >
            <div className='mb-5'>
              <label className={LABEL}>
                Vessel <span className='text-danger'>*</span>
              </label>
              <SearchableSelect
                className={`form-select text-dark ${errors.vesselId ? 'is-invalid' : ''}`}
                value={formData.vesselId}
                onChange={(e) => {
                  setFormData({...formData, vesselId: Number(e.target.value)})
                  if (errors.vesselId) setErrors({...errors, vesselId: ''})
                }}
              >
                <option value=''>Select Vessel</option>
                {vessels.map((v) => (
                  <option key={v.id} value={v.id}>
                    {(v as any).fleet_name || v.name || `Vessel ${v.id}`}
                  </option>
                ))}
              </SearchableSelect>
              {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
            </div>
            <div className='mb-5'>
              <label className={LABEL}>
                Job Plan Name <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className={`form-control text-dark ${errors.name ? 'is-invalid' : ''}`}
                value={formData.name}
                onChange={(e) => {
                  setFormData({...formData, name: e.target.value})
                  if (errors.name) setErrors({...errors, name: ''})
                }}
                placeholder='e.g., Main Engine Maintenance Plan'
              />
              {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
            </div>
            <div className='mb-5'>
              <label className={LABEL}>Description</label>
              <textarea
                className='form-control text-dark'
                rows={3}
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder='Job Plan description (optional)'
              />
            </div>
            <div className='mb-5'>
              <label className={LABEL}>Status</label>
              <SearchableSelect
                className='form-select text-dark'
                value={formData.status}
                onChange={(e) =>
                  setFormData({...formData, status: e.target.value as PmsPlanStatus})
                }
              >
                <option value='DRAFT'>Draft</option>
                <option value='ACTIVE'>Active</option>
                <option value='SUSPENDED'>Suspended</option>
                <option value='ARCHIVED'>Archived</option>
              </SearchableSelect>
            </div>
          </div>
          <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Cancel
            </button>
            <button type='submit' className='btn btn-primary btn-sm'>
              Save Job Plan
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
